import { skipToken } from "@reduxjs/toolkit/query/react";
import { Color } from "@repo/app-types/constants/colors";
import {
  asTeammateId,
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { Size } from "@repo/ui/helpers/helpers";
import { inputStyle } from "@repo/ui/Inputs/input";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { menuStyle } from "@repo/ui/Menu/menuStyle";
import { Pill } from "@repo/ui/Pills/pill";
import { selectStyle } from "@repo/ui/Select/style";
import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { IconSelector } from "@tabler/icons-react";
import { type FC, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Collection,
  Input,
  ListBox,
  ListBoxItem,
  ListBoxLoadMoreItem,
  Popover,
  SearchField,
  Select,
  SelectValue,
} from "react-aria-components";
import {
  useTeammateQuery,
  useTeammatesPaginatedInfiniteQuery,
} from "../../../api/entities/teammate.js";

interface TeammateManagerCellProps {
  managerId: TeammateId | null | undefined;
  isDisabled: boolean;
  onChange: (managerId: TeammateId | null) => void;
}

/**
 * The current manager, rendered the same way wherever they come from. A
 * deactivated manager stays visible and removable but is not offered as a
 * choice, so this is the only place their pill appears.
 */
const ManagerLabel: FC<{
  name: string | undefined;
  isDeactivated: boolean;
}> = ({ name, isDeactivated }) => {
  if (name === undefined) {
    // Hidden from assistive tech rather than announced: `role="status"` is
    // dropped inside a button or an option anyway (both are children-
    // presentational), and a live region per row would be noise. The control
    // keeps its own stable name meanwhile.
    return (
      <span
        className={skeletonStyle({ className: "inline-block h-2 w-24" })}
        aria-hidden
      />
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate">{name}</span>
      {isDeactivated ? (
        <Pill
          label="Deactivated"
          color={Color.Red}
          size={Size.SMALL}
          className="shrink-0"
        />
      ) : null}
    </span>
  );
};

interface ManagerOption {
  readonly id: string;
  readonly name: string;
}

const NONE_OPTION_ID = "_none";

export const TeammateManagerCell: FC<TeammateManagerCellProps> = (props) => {
  // Every manager is resolved here rather than handed in from whatever list the
  // row came from, so the name doesn't depend on how far the roster has been
  // paginated or whether it includes deactivated teammates. The roster warms
  // this cache, so a manager already on screen costs no request.
  const { data: manager, isError } = useTeammateQuery(
    props.managerId ?? skipToken,
  );

  const managerDisplayName = useMemo(() => {
    if (!props.managerId) {
      return undefined;
    }
    if (manager) {
      return buildTeammateDisplayNames(manager).primaryDisplayName;
    }
    // A manager the company can no longer read is still a selected value the
    // control has to render something for.
    return isError ? "Unknown" : undefined;
  }, [props.managerId, manager, isError]);

  const isManagerDeactivated = manager?.deactivated_at != null;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState(search);

  useEffect(() => {
    if (!search) {
      setSearchDebounced("");
      return;
    }

    const timer = setTimeout(() => {
      setSearchDebounced(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const {
    data: teammatesData,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useTeammatesPaginatedInfiniteQuery(
    isOpen ? { search: searchDebounced } : skipToken,
  );

  const teammates = useMemo(
    () => teammatesData?.pages.flatMap((p) => p.teammates) ?? [],
    [teammatesData],
  );

  const options = useMemo<readonly ManagerOption[]>(() => {
    return teammates.map((t) => ({
      id: t.id,
      name: buildTeammateDisplayNames(t).primaryDisplayName,
    }));
  }, [teammates]);

  const isLoading = isFetching && !isFetchingNextPage;

  const css = `
/* Remove the Chrome default clear button */
[type="search"]::-webkit-search-cancel-button,
[type="search"]::-webkit-search-decoration {
  -webkit-appearance: none;
}

/* Work around for Tailwind + React Aria focus selector being broken on Autocomplete. */
[type="search"]:focus {
  --tw-ring-inset: inset;
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);
}
`;

  return (
    <Select
      aria-label="Manager"
      isDisabled={props.isDisabled}
      value={props.managerId ?? NONE_OPTION_ID}
      onChange={(key) => {
        const id = key ? String(key) : null;
        props.onChange(id && id !== NONE_OPTION_ID ? asTeammateId(id) : null);
      }}
      onOpenChange={(newIsOpen) => {
        setIsOpen(newIsOpen);

        if (!newIsOpen) {
          setSearch("");
        }
      }}
    >
      {/*
        The column is shrink-to-fit, so without a ceiling one long name — or
        one deactivated manager's pill — widens it for every row. The cap is
        generous enough that ordinary names still show in full, and it is what
        gives the label's `truncate` something to truncate against.
      */}
      <Button
        className={selectStyle().button({
          className: "max-w-[320px] min-w-[200px]",
        })}
      >
        {props.managerId ? (
          <SelectValue className={selectStyle().value()}>
            <ManagerLabel
              name={managerDisplayName}
              isDeactivated={isManagerDeactivated}
            />
          </SelectValue>
        ) : (
          <SelectValue className={selectStyle().placeholder()}>
            None
          </SelectValue>
        )}
        <IconSelector className={selectStyle().chevron()} />
      </Button>

      <Popover
        className={menuStyle().popover({
          className: "flex h-[400px] w-[300px] flex-col",
        })}
      >
        {/* eslint-disable-next-line react/no-unknown-property */}
        <style scoped>{css}</style>

        <Autocomplete>
          <SearchField aria-label="Search managers" className="p-1">
            <Input
              className={inputStyle().input({
                className: "",
              })}
              placeholder="Search..."
              autoFocus
              autoComplete="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchField>

          <ListBox
            aria-label="Manager options"
            className="flex-1 overflow-y-auto p-1"
            renderEmptyState={() =>
              isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader style={LoaderStyle.SPINNER} />
                </div>
              ) : (
                <div className="py-2 pr-4 pl-4 text-sm text-muted-foreground">
                  No matches found
                </div>
              )
            }
          >
            {/*
              The options below are the active roster, so a deactivated manager
              is never offered as a choice — but the one already assigned is
              pinned here, both to keep the selected value in the collection and
              so it can be seen and swapped out.
            */}
            {props.managerId ? (
              <ListBoxItem
                key={props.managerId}
                id={props.managerId}
                // An empty string reads as no `textValue` at all, which warns
                // and leaves the item unmatchable by type-ahead.
                textValue={managerDisplayName ?? "Manager"}
                className={menuStyle().item()}
              >
                <ManagerLabel
                  name={managerDisplayName}
                  isDeactivated={isManagerDeactivated}
                />
              </ListBoxItem>
            ) : null}

            <ListBoxItem
              key={NONE_OPTION_ID}
              id={NONE_OPTION_ID}
              textValue="None"
              className={menuStyle().item()}
            >
              None
            </ListBoxItem>

            <Collection items={options.filter((o) => o.id !== props.managerId)}>
              {(option) => (
                <ListBoxItem
                  textValue={option.name}
                  className={menuStyle().item()}
                >
                  {option.name}
                </ListBoxItem>
              )}
            </Collection>

            <ListBoxLoadMoreItem
              onLoadMore={hasNextPage ? fetchNextPage : undefined}
              isLoading={isFetchingNextPage}
              className="flex items-center justify-center py-2"
            >
              <Loader style={LoaderStyle.SPINNER} />
            </ListBoxLoadMoreItem>
          </ListBox>
        </Autocomplete>
      </Popover>
    </Select>
  );
};
